import { Component, OnInit, Input, OnDestroy, ElementRef, AfterViewInit } from '@angular/core';
import { Options } from '../options/options.component';
import { Subscription, BehaviorSubject } from 'rxjs';
import { FocusOut } from 'projects/exi-ui/src/lib/utils/focusOut.class';
import { Fade } from 'projects/exi-ui/src/lib/animations/animations';
import { STUB_FN_FALSE } from 'projects/exi-ui/src/lib/utils/stubs';

@Component({
   selector: 'app-searcher',
   template: `
      <input   class="searcher" 
               placeholder="Type to search" 
               [(ngModel)]="searchable"
               (keyup)="searcher.search(searchable)" 
               (keyup.enter)="customSelect()"
               (blur)="focusOut.handler($event)"
               (focus)="focus()"
               [ngClass]="{'invalid': isFocused && !searcher.isSomeFindings}"
               [disabled]="ctx.isInputDisabled()">
      <app-options *ngIf="searcher.isSomeFindings" [ctx]="options" @Fade></app-options>
   `,
   styleUrls: ['./searcher.component.scss'],
   animations: [Fade]
})
export class SearcherComponent<D extends object> implements OnInit, AfterViewInit, OnDestroy {
   @Input() public ctx: ISearcher<D>;
   public searcher: Searcher<D>;
   public searchable = '';
   public options: Options<D>;
   public focusOut: FocusOut;
   public isFocused = false;


   constructor(
      private hostRef: ElementRef<HTMLElement>
   ) {}


   ngOnInit() {
      this.searcher = new Searcher(this.ctx);
      this.searcher.init();
      this.ctx.isInputDisabled = this.ctx.isInputDisabled || STUB_FN_FALSE;

      this.options = {
         isDisabled: this.ctx.isDisabled,
         title: this.ctx.title,
         stream: this.searcher.suggested,
         style: this.ctx.style,
         onBlur: e => this.focusOut.handler(e),
         isSelected: () => false,
         select: option => {
            this.clearSuggested();
            this.ctx.select(option);
         },
      }
   }


   ngAfterViewInit() {
      this.focusOut = new FocusOut({
         host: this.hostRef.nativeElement,
         callback: () => { 
            this.clearSuggested();
            this.isFocused = false;
         }
      });
   }


   ngOnDestroy() {
      this.searcher.destroy();
   }


   public customSelect() {
      const existing = this.searcher.tryTogetFromUnique(this.searchable);

      if (this.searcher.isSomeFindings) {
         if (existing !== null) {
            this.ctx.select(existing);
         } else if (this.ctx.customSelect) {
            this.ctx.customSelect(this.searchable);
            this.clearSuggested();
         }
      }
   }


   public focus() {
      this.isFocused = true;
   }


   private clearSuggested() {
      this.searchable = '';
      this.searcher.clearSuggested();
   }
}

export type SearchMode = 'includes' | 'start' | 'end' | 'euqals';

export interface ISearcher<D extends object> extends Options<D> {
   customSelect?: (searchable: string) => void;
   isInputDisabled?: () => boolean;
   isSelected: (val: D) => boolean;
}

export class Searcher<D extends object> {
   readonly suggested = new BehaviorSubject<D[]>([]);
   private unique: D[] = [];
   private sbcr: Subscription;
   public isSomeFindings = false;


   constructor(
      public ctx: ISearcher<D>
   ) {}


   public search(searchable: string): D[] {
      const suggested: D[] = this.unique
         .filter(opt => this.ctx.title(opt).includes(searchable))
         .filter(opt => !this.ctx.isSelected(opt));

      this.suggested.next(suggested);
      this.isSomeFindings = suggested.length > 0;

      return suggested;
   }


   public clearSuggested() {
      this.suggested.next([]);
   }


   public tryTogetFromUnique(title: string): D | null {
      return this.unique.find(un => this.ctx.title(un) === title) || null;
   }

   public init() {
      this.sbcr = this.ctx.stream.subscribe(options => {
         const uniq = {};
         options.forEach(op => uniq[this.ctx.title(op)] = op);
         this.unique =  Object.values(uniq);
      });
   }

   
   public destroy() {
      this.unique = [];
      this.suggested.next([]);
      this.isSomeFindings = false;
      this.sbcr.unsubscribe();
   }
}